/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import _ from 'lodash';

const GTMEventTarget = 'dataLayer';

declare global {
	interface Window {
		[ GTMEventTarget ]: Array<unknown>;
	}
}

export type GTMEvent = {
	event: string;

	action?: string;
	active?: boolean;
	group?: string;
	isDirty?: boolean;
	option?: string;
	query?: string;
	realTime?: boolean;
	results?: number;
	stage?: string;
	view?: string;

	menuBar?: boolean;
	wrapped?: boolean;
	additionalBlock?: boolean;
	additionalBalloon?: boolean;

	capability?: Array<{
		name: string;
		selected: number;
		total: number;
	}>;
};

// A collection of parameters to clear when sending a new event. See #781 and #547.
const eventDimensions: Record<keyof GTMEvent, undefined> = {
	event: undefined,
	action: undefined,
	active: undefined,
	group: undefined,
	isDirty: undefined,
	option: undefined,
	query: undefined,
	realTime: undefined,
	results: undefined,
	stage: undefined,
	view: undefined,
	menuBar: undefined,
	wrapped: undefined,
	additionalBlock: undefined,
	additionalBalloon: undefined,
	capability: undefined
};

export function triggerGTMEventApp( event: GTMEvent ): void {
	if ( !window[ GTMEventTarget ] ) {
		return;
	}

	if ( !( window[ GTMEventTarget ] instanceof Array ) ) {
		return;
	}

	// Send all details to avoiding interactions between events. See #781 and #547.
	const eventData: GTMEvent = {
		...eventDimensions,
		...snakeCaseStringsDeep( event ) as GTMEvent
	};

	window[ GTMEventTarget ].push( eventData );
}

export function triggerGTMEventPreviewFactory( locationOrigin: string ): typeof triggerGTMEventApp {
	return ( event: GTMEvent ) => {
		window.parent.postMessage(
			{
				GTMEvent: event,
				isGTMEventTrigger: true
			},
			locationOrigin
		);
	};
}

function snakeCaseStringsDeep( item: unknown ): unknown {
	if ( _.isArray( item ) ) {
		return item.map( innerItem => snakeCaseStringsDeep( innerItem ) );
	}

	if ( _.isObject( item ) ) {
		return _.mapValues( item, innerItem => snakeCaseStringsDeep( innerItem ) );
	}

	if ( typeof item === 'string' ) {
		return _.snakeCase( item );
	}

	return item;
}
